import { useToasts } from "@certn/cascadia";
import { useAPIRequest } from "@certn/legacy-api";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { useLanguageOptions } from "./api/useLanguageOptions";
import { useUser } from "./api/useUser";

export function useLanguage() {
  const { show } = useToasts();
  const request = useAPIRequest();
  const { user, mutate } = useUser();
  const { t, i18n } = useTranslation();
  const { languageOptions } = useLanguageOptions();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const locale = searchParams.get("locale");
    if (user) {
      i18n.changeLanguage(user.regional_language);
    } else if (locale) {
      i18n.changeLanguage(locale);
    }
    document.documentElement.lang = i18n.language;
  }, [user]);

  const getLanguageLabel = (locale: string) =>
    languageOptions?.find(({ value }) => value === locale)?.display_value;

  const changeLanguage = async (locale: string) => {
    try {
      if (user) {
        await request("/internal/people/me/language/update/", {
          method: "POST",
          body: JSON.stringify({ language: locale }),
        });
        mutate();
      }
      i18n.changeLanguage(locale);
      document.documentElement.lang = locale;
      window.location.reload();
    } catch (e) {
      console.warn("Unable to update language:", e);
      show({
        title: t("Something went wrong, try again"),
        type: "error",
      });
    }
  };

  return {
    changeLanguage,
    getLanguageLabel,
    currentLanguageLabel: getLanguageLabel(i18n.resolvedLanguage),
  };
}
